import React, { useState } from 'react'
import { Redirect, Link } from 'react-router-dom'
import {
  CAlert,
  CCol,
  CForm,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CButton
} from '@coreui/react';
import { connect, DispatchProp } from "react-redux";
import { useFormik } from 'formik';
import classNames from 'classnames';
import * as Yup from 'yup';
import { some, toPairs } from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';
import AuthUtils from "../../shared/utils/auth-utils";
import EnvironmentUtils from '../../shared/utils/environment-utils';
import { LoginRequest } from "../actions";
import LoadingButton from '../../shared/components/LoadingButton';
import { CreateErrorNotice } from '../../store/actions/toaster.action';

const invalidUsername = 'Invalid email address';
const errorRequired = 'This field is required';
const useCompanySignInLabel = 'Use company sign in';
const useStandardSignInLabel = 'Use standard sign in';

const validationSchema = Yup.object({
  username: Yup.string().email(invalidUsername).required(errorRequired),
  password: Yup.string().required(errorRequired),
});

const validationSSOLoginSchema = Yup.object({
  username: Yup.string().email(invalidUsername).required(errorRequired),
});

const companySigninAllowedDomains = [
  {
    domains: ['virginmedia.com', 'virginmedia.co.uk'], 
    provider: 'virgin'
  },
  {
    domains: ['sky.uk', 'sky.com'],
    provider: 'sky'
  },
  {
    domains: ['ta-petro.com'], 
    provider: process.env.ENV_NAME === 'staging' ? 'ta_uat_pro_console' : 'ta_pro_console'
  },
  {
    domains: ['glympse.com'],
    provider: process.env.ENV_NAME === 'staging' ? 'glympseprostaging' : 'pro_sandbox_test'
  },
  {
    domains: ['tamarac.org'],
    provider: 'tamarac_pro_console'
  }
];


interface LoginFormValues {
  username: string;
  password: string;
}

const initialValues: LoginFormValues = {
  username: '',
  password: '',
};

interface LoginPageProps extends DispatchProp{
  errorText: string;
  isLoading: boolean;
}

const LoginPage = (props: LoginPageProps) => {
  const {
    errorText,
    isLoading,
    dispatch
  } = props;

  const {
    enableForgotPasswordModule
  } = useFlags();

  const [isCompanySignIn, setIsCompanySignIn] = useState(false);

  const getProviderByEmail = (domain: string) => {
    const allowedDomains: Array<any> = companySigninAllowedDomains;
    const result = allowedDomains.find((domainObj) => domainObj.domains.indexOf(domain) !== -1);
    return result && result.provider;
  }

  const handleSubmit = (values: LoginFormValues) => {
    AuthUtils.removeSessionHasExpired();
    if (isCompanySignIn) {
      const domain = values.username.replace(/.*@/, '');
      const provider = getProviderByEmail(domain);
      if (!provider) {
        dispatch(new CreateErrorNotice('Error', `Company login is not available on the domain ${provider}`));
        return;
      }
      const ssoRequestUrl = EnvironmentUtils.getVar('SSO_REQUEST_URL');
      window.location.replace(`${ssoRequestUrl}/${provider}/pro/request`);
    } else {
      dispatch(new LoginRequest(values));
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: isCompanySignIn ? validationSSOLoginSchema : validationSchema,
    onSubmit: handleSubmit,
  });

  React.useEffect(() => {
    toPairs(initialValues).forEach(([field]) => {
      formik.setFieldTouched(field, false);
    });
  }, [errorText]);

  const isInvalid = (fieldName: string) => formik.touched[fieldName] && formik.errors[fieldName];

  const isTouched = () => some(formik.touched, Boolean);

  return (
      <>
        { AuthUtils.isAuth() && (<Redirect to="/" />)}
        { !AuthUtils.isAuth() && (
          <>
            <h1 className="text-center">Login</h1>
            <p className="text-center">With Glympse PRO, you have access to the real-time location of your service technicians and drivers allowing you to optimize routes, capture time on site, and much more. Ultimately improving your business.</p>
            <p className="text-center">Glympse PRO does the heavy lifting for you by processing and analyzing your location data helping you to make better business decisions.</p>
            <p className="text-center">Already have an account, sign in below.</p>
            <p className="text-center">
              <a href="https://glympsepro.com" className="context-link">Don&apos;t have an account? Discover Glympse PRO and request your demo today!</a>
            </p>
            <CForm onSubmit={formik.handleSubmit}>
              { AuthUtils.hasSessionExpired() && <CAlert color="warning">Your session has expired, please log in again.</CAlert> }
              <CFormGroup>
                <CLabel htmlFor="username" className="required">Username</CLabel>
                <CInput
                  id="username"
                  type="text"
                  autoComplete="username"
                  name="username"
                  className={classNames({'is-invalid': isInvalid('username')})}
                  {...formik.getFieldProps('username')}
                />
                {isInvalid('username') && <div className="invalid-feedback">{formik.errors.username}</div>}
              </CFormGroup>
              {
                !isCompanySignIn ? (
                  <CFormGroup>
                    <CLabel htmlFor="password" className="required">Password</CLabel>
                    <CInput
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      name="password"
                      className={classNames({'is-invalid': isInvalid('password')})}
                      {...formik.getFieldProps('password')}
                    />
                    {isInvalid('password') && <div className="invalid-feedback">{formik.errors.password}</div>}
                  </CFormGroup>
                ) : null
              }
              <CRow className="mb-2">
                <CCol>
                  <LoadingButton
                    type="submit"
                    className="bg-color-orange border-0 pl-4 pr-4 loginPage-loginBtn"
                    isLoading={isLoading}
                  >
                    Login
                  </LoadingButton>
                </CCol>
                {
                  enableForgotPasswordModule ? (
                    <CCol className="text-right">
                      <Link to="/password/retrieval" className="context-link">Forgot password?</Link>
                    </CCol>
                  ) : null
                }
              </CRow>
              <CRow className="mt-5">
                <CCol className="text-center">
                  <CButton variant="ghost" className="company-sign-in-button pl-4 pr-4" onClick={() => setIsCompanySignIn((value) => !value)}>{isCompanySignIn ? useStandardSignInLabel : useCompanySignInLabel}</CButton>
                </CCol>
              </CRow>
            </CForm>
            {
              (errorText && !isTouched()) ? (
                <CAlert className="my-10" color="warning" fade>
                  {errorText === 'showErrorHTML' ? (
                    <div>
                      <div>Trouble logging in? Please check the following.</div><br/>
                      <ol>
                        <li>Your account registration may not be complete. Did you click on the account verification link that was emailed to you? This link expires after 4 hours and cannot be resent. If you did not verify your account, you will need to re-register.</li>
                        <li>If you just registered within 4 hours, please be sure to check your email for the account verification message. Click on the link to verify your account within the email.</li>
                        <li>Be sure you are using the correct email and password for your account.</li>
                      </ol>
                      <div>If you have any other issues, please contact Glympse PRO <a href="mailto:prosupport@glympse.com">support</a>!</div>
                    </div>
                  ) : (
                    errorText
                  )}
                </CAlert>
              ) : null
            }
          </>
        )}
      </>
  )
}

const mapStateToProps = (state) => ({
  errorText: state.login && state.login.errorText,
  isLoading: state.login && state.login.isLoading,
});


export default connect(mapStateToProps)(LoginPage);
